import { get } from 'lodash'

import { OtpVerificationChannelStatusAction, OtpVerificationErrorAction, Services, createInitialOtpVerificationChannel, getOtpVerificationChannelStatusAction, getOtpVerificationCodeLength, getOtpVerificationCodeMask, getOtpVerificationEndpointPath, getOtpVerificationErrorAction, getOtpVerificationErrorType, getOtpVerificationExpiresAt, getPositiveFiniteNumber, getServicePathToAPI, isOtpVerificationAbortError, isOtpVerificationCodeChannel, isOtpVerificationCodelessChannel, isOtpVerificationExpiredChannelStatus, joinURLPath, normalizeUnixTimestamp, shouldOtpVerificationSkipSessionClosedError } from '@sigma-frontend-libs/commons'
import { Methods } from '@sigma-frontend-libs/g-fetch'

import { createRequest, globalErrorHandler, proxy } from '@/utils'

import render from './render'

const requestClient = createRequest()
const pathToAPI = getServicePathToAPI(Services['otp-verification'])

const getInitialChannel = () => {
  return {
    ...createInitialOtpVerificationChannel(),
    status: '',
    codeType: ''
  }
}

export default {
  name: 'OTPVerificationDialog',

  mixins: [ proxy() ],

  props: {
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },

  data() {
    return {
      loadingWidget: false,
      loadingSend: false,
      loadingChannel: false,
      loadingStatus: false,
      loadingCodeCheck: false,
      loadingComplete: false,

      firstSend: true,
      firstLoadingChannel: true,

      widget: {
        name: undefined,
        isActive: undefined,
        isBlocked: undefined,
        captchaIsRequired: undefined
      },

      requestId: undefined,
      code: '',

      channel: getInitialChannel(),

      phoneConfirmed: false,
      remainingCodeAttemptsExhausted: false,

      error: '',
      codeError: '',

      resendCooldownExpiresAt: 0,
      resendCooldownTimestamp: Date.now(),
      resendCooldownIntervalId: undefined,

      wasOpened: false,

      currentSessionId: 0,

      channelPollingTimeoutId: undefined,
      statusPollingTimeoutId: undefined
    }
  },

  computed: {
    isWidgetReady() {
      return this.widget.name && this.widget.isActive && !this.widget.isBlocked
    },

    loading() {
      return Boolean(
        this.loadingWidget ||
        this.loadingSend ||
        this.loadingChannel ||
        this.loadingStatus ||
        this.loadingCodeCheck ||
        this.loadingComplete
      )
    },

    description() {
      const translate = key => {
        return this.getTranslate(`${Services['otp-verification']}.descriptions.${key}`)
      }
      switch (true) {
        case this.loadingWidget: return translate('widget')
        case this.loadingSend && this.firstSend: return translate('send')
        case this.loadingChannel && this.firstLoadingChannel: return translate('channel')
        case this.isCodelessChannel: return translate('codeless')
      }
    },

    codeLength() {
      return getOtpVerificationCodeLength(this.channel)
    },
    codeMask() {
      return getOtpVerificationCodeMask(this.codeLength)
    },

    isCodeChannel() {
      return isOtpVerificationCodeChannel(this.channel)
    },
    isCodelessChannel() {
      return isOtpVerificationCodelessChannel(this.channel)
    },
    isCodeSended() {
      return Boolean(this.requestId && this.channel.status === 'sent')
    },

    remainingCodeAttempts() {
      return getPositiveFiniteNumber(get(this.channel, 'remainingCodeAttempts', 0))
    },

    canContinueVerification() {
      return Boolean(this.proxy && this.requestId && !this.phoneConfirmed)
    },

    resendDisabled() {
      return Boolean(
        this.requestId &&
        !this.loadingWidget &&
        !this.resendCooldownLeftSeconds &&
        !this.phoneConfirmed
      )
    },
    resendCooldownLeftSeconds() {
      const millisecondsLeft = this.resendCooldownExpiresAt - this.resendCooldownTimestamp

      if (millisecondsLeft > 0) {
        return Math.ceil(millisecondsLeft / 1000)
      }

      return 0
    },

    checkCodeDisabled() {
      const code = this.code || ''

      if (!this.isCodeChannel) {
        return true
      }

      if (!this.requestId || this.phoneConfirmed || this.remainingCodeAttemptsExhausted) {
        return true
      }

      if (this.loadingCodeCheck) {
        return true
      }

      if (this.remainingCodeAttempts <= 0) {
        return true
      }

      if (this.codeLength > 0) {
        return code.length !== this.codeLength
      }

      return !code || this.codeError
    },

    codeLifetimeSeconds() {
      return getPositiveFiniteNumber(get(this.channel, 'settings.lifetime', 0))
    },
    showCodeLifetimeInfo() {
      return Boolean(
        !this.remainingCodeAttemptsExhausted &&
        this.requestId &&
        this.isCodeChannel &&
        this.codeLifetimeSeconds
      )
    },
    showCodeInput() {
      return Boolean(
        this.isCodeChannel &&
        !this.remainingCodeAttemptsExhausted &&
        !this.phoneConfirmed &&
        !this.error
      )
    },

    attemptsInfo() {
      if (!this.isCodeChannel) {
        return undefined
      }

      return this.getTranslate(`${Services['otp-verification']}.hints.remainingAttempts`, {
        count: this.remainingCodeAttempts
      })
    },

    successInfo() {
      if (!this.phoneConfirmed) {
        return undefined
      }

      return this.getTranslate(`${Services['otp-verification']}.infos.confirmed`)
    },

    statusInfo() {
      const translate = key => {
        return this.getTranslate(`${Services['otp-verification']}.descriptions.${key}`)
      }
      if (this.error || this.phoneConfirmed || this.showCodeInput || this.showCodeLifetimeInfo) {
        return undefined
      }

      switch (true) {
        case this.loadingWidget: return translate('widget')
        case this.loadingSend: return translate('send')
        case this.loadingChannel:
        case this.loadingStatus: return translate('channel')
        case this.isCodelessChannel && this.canContinueVerification: return translate('codeless')
        case Boolean(this.requestId): return translate('channel')
        default: return translate('send')
      }
    }
  },

  beforeDestroy() {
    this.reset()
  },

  watch: {
    proxy(value) {
      if (value) {
        this.wasOpened = true
        this.init()
      }
    }
  },

  methods: {
    getPathToApi() {
      return joinURLPath(pathToAPI, getOtpVerificationEndpointPath())
    },
    ensureActiveSession(sessionId) {
      return this.currentSessionId === sessionId && this.proxy
    },

    restartLongPollingBySource(source) {
      switch (source) {
        case 'status': return this.startStatusPollingTimeout()
        case 'channel': return this.startChannelPollingTimeout()
        default: return
      }
    },
    applyChannelData(channel) {
      this.firstLoadingChannel = false
      this.channel.label = channel.label
      this.channel.status = channel.status
      this.channel.codeType = channel.codeType
      this.channel.settings = channel.settings || this.channel.settings
      this.channel.remainingCodeAttempts = Number(channel.remainingCodeAttempts || 0)
    },
    async processChannelStatus(longPolling) {
      const channelStatusAction = getOtpVerificationChannelStatusAction(
        this.channel.status,
        this.isCodelessChannel,
        longPolling
      )

      if (channelStatusAction === OtpVerificationChannelStatusAction.complete) {
        return await this.checkStatusAndComplete()
      }

      if (isOtpVerificationExpiredChannelStatus(this.channel.status) || this.channel.remainingCodeAttempts <= 0) {
        return this.setRemainingCodeAttemptsExhausted()
      }

      switch (channelStatusAction) {
        case OtpVerificationChannelStatusAction.pollStatus: return await this.checkStatus(true)
        case OtpVerificationChannelStatusAction.showSessionClosedError: {
          this.error = this.getTranslate(`${Services['otp-verification']}.errors.SessionClosedException`)

          return
        }
        case OtpVerificationChannelStatusAction.pollChannel: {
          this.startChannelPollingTimeout()

          return
        }
      }
    },

    startChannelPollingTimeout() {
      this.resetChannelPollingTimeout()
      if (!this.canContinueVerification) {
        return
      }

      this.channelPollingTimeoutId = setTimeout(async () => {
        await this.getChannel(true, true)
      }, 1000)
    },
    startStatusPollingTimeout() {
      this.resetStatusPollingTimeout()
      if (!this.canContinueVerification) {
        return
      }

      this.statusPollingTimeoutId = setTimeout(async () => {
        await this.checkStatus(true)
      }, 1000)
    },
    startResendCooldownInterval() {
      this.resetResendCooldownInterval()
      this.resendCooldownTimestamp = Date.now()

      this.resendCooldownIntervalId = setInterval(() => {
        this.resendCooldownTimestamp = Date.now()
        if (!this.resendCooldownLeftSeconds) {
          this.resetResendCooldownInterval()
        }
      }, 1000)
    },

    checkRemainingCodeAttempts() {
      if (this.phoneConfirmed || this.channel.status === 'confirmed') {
        return
      }

      if (this.remainingCodeAttempts <= 0) {
        this.setRemainingCodeAttemptsExhausted()
      }
    },
    shouldKeepInitialChannelLoading(error) {
      if (!this.firstLoadingChannel) {
        return false
      }

      return getOtpVerificationErrorAction(getOtpVerificationErrorType(error), 'channel') ===
        OtpVerificationErrorAction.restartChannelPolling
    },

    setResendCooldownExpiresAt(expiresAt) {
      this.resendCooldownExpiresAt = normalizeUnixTimestamp(expiresAt)
      if (this.resendCooldownExpiresAt > Date.now()) {
        this.startResendCooldownInterval()
      } else {
        this.resendCooldownExpiresAt = 0
        this.resetResendCooldownInterval()
      }
    },
    setDefaultResendCooldown() {
      this.setResendCooldownExpiresAt(Date.now() + 60 * 1000)
    },
    setResendCooldownFromError(error) {
      const expiresAt = getOtpVerificationExpiresAt(error)
      if (expiresAt) {
        return this.setResendCooldownExpiresAt(expiresAt)
      }
    },
    setRemainingCodeAttemptsExhausted() {
      if (this.phoneConfirmed || this.channel.status === 'confirmed') {
        return
      }

      this.reset()
      this.remainingCodeAttemptsExhausted = true
      this.error = this.getTranslate(`${Services['otp-verification']}.errors.remainingCodeAttempts`)
    },

    resetChannelPollingTimeout() {
      if (this.channelPollingTimeoutId) {
        clearTimeout(this.channelPollingTimeoutId)
        this.channelPollingTimeoutId = undefined
      }
    },
    resetStatusPollingTimeout() {
      if (this.statusPollingTimeoutId) {
        clearTimeout(this.statusPollingTimeoutId)
        this.statusPollingTimeoutId = undefined
      }
    },
    getLongPollingAbortKey() {
      return [ Services['otp-verification'], 'long-polling', this._uid ].join(':')
    },
    resetLongPollingRequests() {
      this.resetChannelPollingTimeout()
      this.resetStatusPollingTimeout()
      if (typeof requestClient.abort === 'function') {
        requestClient.abort(Methods.GET, this.getLongPollingAbortKey())
      }
    },
    resetResendCooldownInterval() {
      if (this.resendCooldownIntervalId) {
        clearInterval(this.resendCooldownIntervalId)
        this.resendCooldownIntervalId = undefined
      }
    },
    resetCode(hard = false) {
      this.code = ''
      if (hard) {
        this.resetErrors()
      }
    },
    resetErrors() {
      this.codeError = ''
      this.error = ''
    },
    resetChannel() {
      this.channel = getInitialChannel()
    },
    resetExpires() {
      this.resendCooldownExpiresAt = 0
      this.resendCooldownTimestamp = Date.now()
    },

    reset(hard = true) {
      this.currentSessionId += 1
      this.resetLongPollingRequests()
      this.resetResendCooldownInterval()
      this.resetCode()

      this.loadingWidget = false
      this.loadingSend = false
      this.loadingChannel = false
      this.loadingStatus = false
      this.loadingCodeCheck = false
      this.loadingComplete = false

      this.requestId = undefined
      this.phoneConfirmed = false
      this.remainingCodeAttemptsExhausted = false
      this.firstSend = true
      this.firstLoadingChannel = true

      this.resetChannel()
      this.resetExpires()

      if (hard) {
        this.resetErrors()
      }
    },

    eventHandler(event) {
      this.proxy = event
      if (event === true) {
        this.wasOpened = true
      }

      if (event === false && this.wasOpened) {
        this.wasOpened = false
        this.reset()
      }
    },

    getErrorText(error) {
      const errorType = getOtpVerificationErrorType(error)
      if (errorType) {
        const options = {}
        switch (errorType) {
          case 'RateLimitException':
          case 'RestartCooldownActiveException': {
            const expiresAt = normalizeUnixTimestamp(getOtpVerificationExpiresAt(error))
            if (expiresAt > Date.now()) {
              options.timeout = Math.ceil((expiresAt - Date.now()) / 1000) + this.getTranslate('misc.measure.time.short.seconds')
            }
            break
          }
        }

        return this.getTranslate(`${Services['otp-verification']}.errors.${errorType}`, options)
      }

      return get(error, 'message') || this.getTranslate('errors.unknown.type')
    },
    shouldSkipSessionClosedError(error, source) {
      return shouldOtpVerificationSkipSessionClosedError(error, source, this.phoneConfirmed)
    },

    async errorHandler(error, source) {
      if (isOtpVerificationAbortError(error)) {
        return
      }

      if (this.shouldSkipSessionClosedError(error, source)) {
        this.resetLongPollingRequests()

        return
      }

      const errorType = getOtpVerificationErrorType(error)
      switch (getOtpVerificationErrorAction(errorType, source)) {
        case OtpVerificationErrorAction.stopPollingAndShowError: {
          this.resetLongPollingRequests()
          this.error = this.getErrorText(error)
          break
        }
        case OtpVerificationErrorAction.resend: return await this.resend(true)
        case OtpVerificationErrorAction.restartChannelPolling: return this.restartLongPollingBySource('channel')
        case OtpVerificationErrorAction.restartStatusPolling: return this.restartLongPollingBySource('status')
        case OtpVerificationErrorAction.refreshChannelAndShowInvalidCode: {
          this.codeError = this.getTranslate(`${Services['otp-verification']}.errors.invalidCode`)

          if (this.requestId) {
            await this.getChannel(false, true)
          }

          this.checkRemainingCodeAttempts()
          break
        }
        case OtpVerificationErrorAction.setRemainingAttemptsExhausted: {
          this.setRemainingCodeAttemptsExhausted()
          break
        }
        case OtpVerificationErrorAction.applyCooldownAndShowError: {
          this.setResendCooldownFromError(error)
          this.error = this.getErrorText(error)
          break
        }
        case OtpVerificationErrorAction.resetAndShowError: {
          this.reset()
          this.error = this.getErrorText(error)
          break
        }
        default: {
          this.resetLongPollingRequests()
          this.error = this.getErrorText(error)
          break
        }
      }

      globalErrorHandler(error)
    },

    async getWidgetData() {
      const sessionId = this.currentSessionId

      try {
        this.loadingWidget = true
        const response = await requestClient.find(joinURLPath(this.getPathToApi(), 'getWidgetData'))
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        if (response) {
          this.widget = response
        }
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          await this.errorHandler(error, 'widget')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingWidget = false
        }
      }
    },
    async send() {
      const sessionId = this.currentSessionId

      try {
        this.loadingSend = true
        this.resetErrors()
        this.remainingCodeAttemptsExhausted = false
        const response = await requestClient.create(joinURLPath(this.getPathToApi(), 'send'))
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        const requestId = get(response, 'requestId')
        if (requestId) {
          this.requestId = requestId
          this.setDefaultResendCooldown()
          await this.getChannel(true)
        }
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          await this.errorHandler(error, 'send')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingSend = false
          this.firstSend = false
        }
      }
    },
    async resend(exception = false) {
      if (!this.requestId || !this.resendDisabled && !exception) {
        return
      }

      const sessionId = this.currentSessionId

      try {
        this.loadingSend = true
        this.resetErrors()
        this.remainingCodeAttemptsExhausted = false
        this.resetCode()
        this.channel.remainingCodeAttempts = Number(get(this.channel, 'settings.numberOfTries', 0))
        await requestClient.create(joinURLPath(this.getPathToApi(), 'resend'), { body: { requestId: this.requestId } })
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        this.setDefaultResendCooldown()
        await this.getChannel(true)
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          await this.errorHandler(error, 'resend')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingSend = false
        }
      }
    },
    async getChannel(longPolling = false, silent = false) {
      if (!this.requestId) {
        return
      }

      const sessionId = this.currentSessionId
      let shouldKeepLoadingChannel = false
      const abortKey = this.getLongPollingAbortKey()

      try {
        this.resetChannelPollingTimeout()
        this.loadingChannel = !silent || this.firstLoadingChannel
        const response = await requestClient.find(joinURLPath(this.getPathToApi(), this.requestId, 'getChannel'), { abortKey })
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        const channel = response
        if (!channel) {
          return
        }
        this.applyChannelData(channel)
        this.checkRemainingCodeAttempts()
        await this.processChannelStatus(longPolling)
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          shouldKeepLoadingChannel = this.shouldKeepInitialChannelLoading(error)
          await this.errorHandler(error, 'channel')
        }
      } finally {
        if (this.ensureActiveSession(sessionId) && !shouldKeepLoadingChannel) {
          this.loadingChannel = false
        }
      }
    },
    async checkStatus(longPolling = false) {
      if (!this.requestId) {
        return
      }

      const sessionId = this.currentSessionId
      const abortKey = this.getLongPollingAbortKey()

      try {
        this.resetStatusPollingTimeout()
        this.loadingStatus = true
        const response = await requestClient.find(joinURLPath(this.getPathToApi(), this.requestId, 'checkStatus'), { abortKey })
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        const status = get(response, 'success')
        if (status) {
          return await this.checkStatusAndComplete()
        }
        if (longPolling) {
          this.startStatusPollingTimeout()
        }
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          await this.errorHandler(error, 'status')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingStatus = false
        }
      }
    },
    async checkCode() {
      if (this.checkCodeDisabled) {
        return
      }

      const sessionId = this.currentSessionId

      try {
        this.loadingCodeCheck = true
        this.resetErrors()
        const response = await requestClient.create(joinURLPath(this.getPathToApi(), 'checkCode'), {
          body: {
            requestId: this.requestId,
            code: this.code
          }
        })
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        const status = get(response, 'success')
        if (status) {
          await this.checkStatusAndComplete()
        }
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingCodeCheck = false
          await this.errorHandler(error, 'checkCode')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingCodeCheck = false
        }
      }
    },
    async checkStatusAndComplete() {
      const sessionId = this.currentSessionId

      try {
        this.loadingComplete = true
        const response = await requestClient.create(joinURLPath(this.getPathToApi(), 'checkStatusAndComplete'), { body: { requestId: this.requestId } })
        if (!this.ensureActiveSession(sessionId)) {
          return
        }

        const status = get(response, 'success')
        if (status) {
          this.resetErrors()
          this.remainingCodeAttemptsExhausted = false
          this.phoneConfirmed = true
          this.resetLongPollingRequests()
          this.resetResendCooldownInterval()
          this.resetExpires()
          this.$emit('confirmed')
        }
      } catch (error) {
        if (this.ensureActiveSession(sessionId)) {
          await this.errorHandler(error, 'checkStatusAndComplete')
        }
      } finally {
        if (this.ensureActiveSession(sessionId)) {
          this.loadingComplete = false
        }
      }
    },

    async init() {
      this.reset()
      const sessionId = this.currentSessionId
      await this.getWidgetData()
      if (!this.ensureActiveSession(sessionId)) {
        return
      }

      if (this.isWidgetReady) {
        await this.send()
      } else {
        this.error = this.getTranslate(`${Services['otp-verification']}.errors.widgetNotReady`)
      }
    }
  },

  render
}
